// use yup to generate validation schema
import * as yup from 'yup';
import { AppointmentOption } from 'src/hooks/useAppointmentOptions/types';
import { ColumnOption, NO_PREFERENCE } from 'src/hooks/useAppointmentOptions/extractColumnOptions';
import {
  additionalInfoSchema,
  clientSchema,
  notificationSchema,
  patientSchema
} from 'src/module/FormKit/validators';

const columnSchema = yup.object().shape({
  label: yup.string().nullable(),
  value: yup.string().nullable(),
  categoryId: yup.number().nullable(),
  appointmentLength: yup.number().nullable(),
  colKey: yup.string().nullable(),
  duplicateOfAppointmentCategoryId: yup.string().nullable()
});

export const appointmentSchema = yup.object().shape({
  useAppointmentTypes: yup.boolean(),
  appointmentOption: yup.object().when('useAppointmentTypes', {
    is: (useAppointmentTypes: boolean) => !!useAppointmentTypes,
    then: yup.object().required('errors.selectionRequired')
  }),
  allowCategorySelection: yup.boolean(),
  columnOption: yup.object().when('allowCategorySelection', {
    is: (allowCategorySelection: boolean) => !!allowCategorySelection,
    then: columnSchema.required('errors.selectionRequired').test({
      name: 'isValidCategory',
      message: 'errors.notValidCategory',
      test: function (value: ColumnOption) {
        const { appointmentOption } = this.parent;
        if (appointmentOption as AppointmentOption) {
          return (
            value.value === NO_PREFERENCE ||
            appointmentOption.categories?.includes(value.colKey) ||
            appointmentOption.categories?.includes(value.duplicateOfAppointmentCategoryId)
          );
        }
        return true;
      }
    })
  }),
  preferredTime: yup
    .string()
    .required('errors.timeRequired')
    .when(['preferredDate', 'availableDates'], {
      is: (preferredDate: string | undefined, availableDates: string[] | undefined) => {
        return preferredDate && availableDates?.includes(preferredDate);
      },
      then: (schema) =>
        schema.test({
          name: 'isValidTime',
          message: 'errors.timeNotAvailable',
          test: function (value) {
            const { availableTimes = [] } = this.parent;
            return !!availableTimes?.includes(value);
          }
        })
    }),
  preferredDate: yup
    .string()
    .required('errors.dateRequired')
    .when(['allowCategorySelection', 'columnOption', 'appointmentOption', 'useAppointmentTypes'], {
      is: (
        allowCategorySelection: boolean,
        columnOption: ColumnOption | undefined,
        appointmentOption: AppointmentOption | undefined,
        useAppointmentTypes: boolean | undefined
      ) => {
        if (!allowCategorySelection) {
          return false;
        } else if (useAppointmentTypes) {
          return !!appointmentOption?.categories?.find((cat) => cat === columnOption?.colKey);
        }
        return true;
      },
      then: yup.string().test({
        name: 'isValidDate',
        message: 'errors.dateNotAvailable',
        test: function (value) {
          const { availableDates = [] } = this.parent;
          return !!availableDates?.includes(value);
        }
      })
    }),
  availableDates: yup.array().of(yup.string()),
  availableTimes: yup.array().of(yup.string()),
  availableColumns: yup.array().of(columnSchema)
});

export const validationSchema = yup.object().shape({
  clientInfo: clientSchema,
  patientInfo: patientSchema,
  appointmentInfo: appointmentSchema,
  additionalInfo: additionalInfoSchema,
  notificationSettings: notificationSchema
});
